// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../context/AnalyticsContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProviderCard from '../components/analytics/ProviderCard';
import ProviderSetupWizard from '../components/analytics/ProviderSetupWizard';
import SchemaExplorer from '../components/analytics/SchemaExplorer';
import AnalyticsChatInterface from '../components/analytics/AnalyticsChatInterface';
import AnalyticsChatSuggestions from '../components/analytics/AnalyticsChatSuggestions';
import AnalyticsErrorHandler from '../components/analytics/AnalyticsErrorHandler';
import AnalyticsValidation from '../components/analytics/AnalyticsValidation';
import { Settings, Database, MessageCircle, TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';

type TabType = 'overview' | 'setup' | 'schema' | 'chat';
type SetupProvider = 'zoho' | 'powerbi' | null;

const AnalyticsPage: React.FC = () => {
  const { state, actions } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [setupProvider, setSetupProvider] = useState<SetupProvider>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check for OAuth callback parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'true') {
      setMessage({ type: 'success', text: 'OAuth authorization successful! You can now use analytics features.' });
      setSearchParams({});
      actions.loadConfiguration();
    } else if (error === 'true') {
      setMessage({ type: 'error', text: 'OAuth authorization failed. Please try again.' });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, actions]);

  const handleSetupProvider = (provider: 'zoho' | 'powerbi') => {
    setSetupProvider(provider);
    setActiveTab('setup');
  };

  const handleSetupComplete = () => {
    setSetupProvider(null);
    setActiveTab('schema');
    setMessage({ type: 'success', text: 'Provider setup completed successfully!' });
  };

  const handleSetupCancel = () => {
    setSetupProvider(null);
    setActiveTab('overview');
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: TrendingUp },
    { id: 'setup' as const, label: 'Setup', icon: Settings, disabled: !setupProvider },
    { id: 'schema' as const, label: 'Schema', icon: Database, disabled: !Object.values(state.providers).some((p: any) => p.authorized) },
    { id: 'chat' as const, label: 'Analytics Chat', icon: MessageCircle, disabled: !state.isAnalyticsChatEnabled },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'setup':
        return renderSetup();
      case 'schema':
        return renderSchema();
      case 'chat':
        return renderChat();
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => {
    const connectedProviders = Object.entries(state.providers).filter(([_, provider]: [string, any]) => provider.authorized);
    const totalTables = Object.values(state.schema).reduce((acc, schema: any) => 
      acc + (schema?.data?.tables?.length || 0), 0
    );

    return (
      <div className="space-y-6">
        {/* Analytics Status Summary */}
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {connectedProviders.length}
              </h3>
              <p className="text-sm text-text-secondary">Connected Providers</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Database className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {totalTables}
              </h3>
              <p className="text-sm text-text-secondary">Data Tables Available</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {state.conversations.length}
              </h3>
              <p className="text-sm text-text-secondary">Analytics Conversations</p>
            </div>
          </div>
        </Card>

        {/* Provider Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          <ProviderCard
            provider="zoho"
            onSetup={() => handleSetupProvider('zoho')}
            onManage={() => handleSetupProvider('zoho')}
          />
          <ProviderCard
            provider="powerbi"
            onSetup={() => handleSetupProvider('powerbi')}
            onManage={() => handleSetupProvider('powerbi')}
          />
        </div>

        {/* Quick Actions */}
        {connectedProviders.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Button
                onClick={() => setActiveTab('schema')}
                variant="outline"
                className="flex items-center justify-center p-4 h-auto"
              >
                <Database className="w-5 h-5 mr-2" />
                <div className="text-left">
                  <div className="font-medium">Explore Schema</div>
                  <div className="text-sm text-text-secondary">Browse your data structure</div>
                </div>
              </Button>
              
              {state.isAnalyticsChatEnabled && (
                <Button
                  onClick={() => setActiveTab('chat')}
                  className="flex items-center justify-center p-4 h-auto"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  <div className="text-left">
                    <div className="font-medium">Start Analytics Chat</div>
                    <div className="text-sm opacity-75">Ask questions about your data</div>
                  </div>
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderSetup = () => {
    if (!setupProvider) {
      return (
        <div className="text-center py-8">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Provider Selected</h3>
          <p className="text-text-secondary mb-4">
            Please select a provider from the overview tab to begin setup.
          </p>
          <Button onClick={() => setActiveTab('overview')} variant="outline">
            Back to Overview
          </Button>
        </div>
      );
    }

    return (
      <ProviderSetupWizard
        provider={setupProvider}
        onComplete={handleSetupComplete}
        onCancel={handleSetupCancel}
      />
    );
  };

  const renderSchema = () => {
    const connectedProviders = Object.entries(state.providers)
      .filter(([_, provider]: [string, any]) => provider.authorized)
      .map(([name]) => name as 'zoho' | 'powerbi');

    if (connectedProviders.length === 0) {
      return (
        <div className="text-center py-8">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Connected Providers</h3>
          <p className="text-text-secondary mb-4">
            Please connect an analytics provider first to explore your data schema.
          </p>
          <Button onClick={() => setActiveTab('overview')} variant="outline">
            Back to Overview
          </Button>
        </div>
      );
    }

    return <SchemaExplorer provider={connectedProviders[0]} />;
  };

  const renderChat = () => {
    const connectedProviders = Object.entries(state.providers)
      .filter(([_, provider]: [string, any]) => provider.authorized)
      .map(([name]) => name as 'zoho' | 'powerbi');

    if (!state.isAnalyticsChatEnabled || connectedProviders.length === 0) {
      return (
        <div className="text-center py-8">
          <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Analytics Chat Not Available</h3>
          <p className="text-text-secondary mb-4">
            {connectedProviders.length === 0
              ? 'Please connect an analytics provider and discover your schema first.'
              : 'Please discover your data schema to enable analytics conversations.'
            }
          </p>
          <Button onClick={() => setActiveTab('overview')} variant="outline">
            Back to Overview
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <AnalyticsChatSuggestions 
          provider={connectedProviders[0]}
          onSuggestionClick={(suggestion) => {
            // Handle suggestion click - could start a new conversation or add to existing
            void suggestion;
          }}
        />
        <AnalyticsChatInterface provider={connectedProviders[0]} />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Analytics Integration</h1>
          <p className="text-text-secondary mt-2">
            Connect and configure external analytics providers for enhanced insights.
          </p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <Card className="p-4">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            const isDisabled = tab.disabled;
            
            return (
              <button
                key={tab.id}
                onClick={() => !isDisabled && setActiveTab(tab.id)}
                disabled={isDisabled}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : isDisabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Tab Content */}
      <div>{renderTabContent()}</div>
    </div>
  );
};

export default AnalyticsPage;